import React, { useMemo, useState } from "react";
import { useL } from "@/lib/useL";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InfoTip } from "@/components/InfoTip";
import { renderSummary } from "@/lib/renderSummary";
import { verifySummary, citeHtml, stem } from "@/lib/sourceMatch";

const markWords = (text, tok) =>
  text.split(/(\s+)/).map((w, i) => {
    const n = w.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
    return n.length >= 4 && tok.has(stem(n)) ? <mark key={i} className="bg-[#002FA7]/15 text-inherit px-0.5">{w}</mark> : w;
  });

const FidelityBadge = ({ v }) => {
  const L = useL();
  if (v.status === "notion")
    return <p className="text-xs text-muted-foreground mb-4" data-testid="fidelity-notion">{L("Explication générale : aucun texte source à vérifier.", "General explanation: no source text to check.")}</p>;
  if (v.status === "unreliable")
    return <p className="text-xs text-muted-foreground mb-4" data-testid="fidelity-unreliable">{L("Vérification indisponible : le résumé est traduit ou très reformulé.", "Check unavailable: the summary is translated or heavily rephrased.")}</p>;
  if (v.status !== "ok") return null;
  const miss = v.cites.length - v.matched;
  return (
    <div className="mb-5" data-testid="fidelity-badge">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <ShieldCheck className="w-4 h-4 text-[#0a7a2f]" />
        <b data-testid="fidelity-score">{L("Fidélité", "Accuracy")} {v.fidelity} %</b>
        <span className="text-muted-foreground">· {L(`${v.matched} point(s) sur ${v.cites.length} retrouvé(s) dans le texte`, `${v.matched} of ${v.cites.length} point(s) found in the text`)}</span>
        <InfoTip testid="fidelity-info" content={L(<><b>Vérifier dans le texte</b> : chaque phrase du résumé porte un numéro [1], [2]… Cliquez dessus pour voir le passage exact du texte d'origine, mots communs surlignés. Un <b>[?]</b> signale un point non retrouvé mot pour mot : il peut s'agir d'une reformulation, à vérifier. Gratuit, calculé sans IA.</>, <><b>Check against the text</b>: each sentence of the summary has a number [1], [2]… Click it to see the exact passage of the original text, shared words highlighted. A <b>[?]</b> marks a point not found word for word: it may be a rephrasing, worth checking. Free, computed without AI.</>)} />
      </div>
      {miss > 0 && (
        <p className="mt-1 text-xs text-[#B45309] flex items-center gap-1" data-testid="fidelity-alert">
          <AlertTriangle className="w-3 h-3" /> {L(`${miss} point(s) non retrouvé(s) mot pour mot : cliquez sur [?] pour vérifier.`, `${miss} point(s) not found word for word: click [?] to check.`)}
        </p>
      )}
    </div>
  );
};

// Summary HTML with clickable source citations and a fidelity badge.
export function VerifiedSummary({ summary, source, format, className, testid, articleRef }) {
  const v = useMemo(() => verifySummary(summary, source, format), [summary, source, format]);
  const html = useMemo(() => (v.status === "ok" ? citeHtml(renderSummary(v.md), v.cites) : renderSummary(summary)), [v, summary]);
  const [open, setOpen] = useState(null);
  const L = useL();
  const onClick = (e) => {
    const b = e.target.closest("[data-cite]");
    if (b) setOpen(v.cites[Number(b.dataset.cite) - 1]);
  };
  const s = v.idx?.sentences || [];
  return (
    <>
      <FidelityBadge v={v} />
      <article ref={articleRef} className={className} translate="no" data-testid={testid} onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="cite-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle>{open.matched ? L(`Passage [${open.n}] dans le texte d'origine`, `Passage [${open.n}] in the original text`) : L("Point non retrouvé mot pour mot", "Point not found word for word")}</DialogTitle>
              </DialogHeader>
              <p className="text-sm border-l-4 border-[#002FA7] pl-3 italic" data-testid="cite-claim">{open.claim}</p>
              {open.i >= 0 ? (
                <div className="text-sm leading-relaxed bg-muted/40 p-4 max-h-[50vh] overflow-y-auto" data-testid="cite-passage">
                  {s[open.i - 1] && <span className="text-muted-foreground">{s[open.i - 1].text} </span>}
                  <span className="font-medium">{markWords(s[open.i].text, open.tok)} {open.span === 2 && s[open.i + 1] ? markWords(s[open.i + 1].text, open.tok) : null}</span>
                  {s[open.i + open.span] && <span className="text-muted-foreground"> {s[open.i + open.span].text}</span>}
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground" data-testid="cite-score">
                {open.matched
                  ? L(`${Math.round(open.score * 100)} % des mots importants de cette phrase se retrouvent dans ce passage.`, `${Math.round(open.score * 100)}% of the important words in this sentence appear in this passage.`)
                  : L(`Passage le plus proche : seulement ${Math.round(open.score * 100)} % de mots communs. Il peut s'agir d'une reformulation ou d'un ajout de l'IA : vérifiez dans votre document.`, `Closest passage: only ${Math.round(open.score * 100)}% shared words. It may be a rephrasing or something added by the AI: check your document.`)}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default VerifiedSummary;
